import { useState, useEffect } from 'react';
import axios from 'axios';
import { useWorkspace } from '../../../../context/WorkspaceContext';
import { toast } from 'react-toastify';
import { FaHashtag, FaPencilAlt, FaCheck } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const SaveViralModal = ({ closeModal, otherInfo }) => {
    const { activeWorkspace, idToken } = useWorkspace();
    const { content, type } = otherInfo || {};
    
    const [templateName, setTemplateName] = useState('');
    const [saveType, setSaveType] = useState(type || 'caption');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        // Pre-fill name based on content
        if (content) {
            const defaultName = content.title 
                ? `${content.title.slice(0, 30)}${content.title.length > 30 ? '...' : ''}`
                : `Viral ${content.platform} - ${new Date().toLocaleDateString()}`;
            setTemplateName(defaultName);
        }
    }, [content]);

    const getCaptionContent = () => {
        return content?.description || '';
    };

    const getHashtagsContent = () => {
        if (content?.hashtags && content.hashtags.length > 0) {
            return content.hashtags.map(tag => 
                tag.startsWith('#') ? tag : `#${tag}`
            ).join(' ');
        }
        return '';
    };

    const handleSave = async () => {
        if (!idToken || !activeWorkspace || !templateName.trim()) {
            toast.error('Please enter a template name');
            return;
        }

        setIsSaving(true);
        try {
            const savePromises = [];

            // Save caption template
            if (saveType === 'caption' || saveType === 'both') {
                const captionData = {
                    name: saveType === 'both' ? `${templateName} - Caption` : templateName,
                    info: getCaptionContent(),
                    type: 'caption',
                    caption: getCaptionContent(),
                    title: saveType === 'both' ? `${templateName} - Caption` : templateName,
                    workspaceId: activeWorkspace.id,
                    viralContentId: content?.id,
                };
                savePromises.push(
                    axios.post(`${BACKEND_API_URL}/api/auth/user/templates/captions`, captionData, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    })
                );
            }

            // Save hashtag template
            if (saveType === 'hashtag' || saveType === 'both') {
                const hashtagContent = getHashtagsContent();
                const hashtags = hashtagContent.match(/#[\w\d]+/g) || [];
                const hashtagData = {
                    name: saveType === 'both' ? `${templateName} - Hashtags` : templateName,
                    info: hashtagContent,
                    type: 'hashtag',
                    hashtags: hashtags,
                    title: saveType === 'both' ? `${templateName} - Hashtags` : templateName,
                    workspaceId: activeWorkspace.id,
                    viralContentId: content?.id,
                };
                savePromises.push(
                    axios.post(`${BACKEND_API_URL}/api/auth/user/templates/hashtag`, hashtagData, {
                        headers: { Authorization: `Bearer ${idToken}` }
                    })
                );
            }

            await Promise.all(savePromises);
            
            const savedTypes = saveType === 'both' ? 'caption and hashtag templates' : `${saveType} template`;
            toast.success(`Successfully saved ${savedTypes}!`);
            closeModal();
        } catch (error) {
            console.error('Error saving template:', error);
            toast.error(error.response?.data?.error || 'Failed to save template');
        } finally {
            setIsSaving(false);
        }
    };

    if (!content) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">No content to save</p>
            </div>
        );
    }

    return (
        <div className="--theme-colors px-6 py-5 w-[500px] max-w-full">
            <div className="flex items-center gap-2 mb-4">
                <HiOutlineSparkles className="text-yellow-500" size={24} />
                <h2 className="text-xl font-semibold">Save Viral Content</h2>
            </div>

            {/* Template Name */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name
                </label>
                <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter a name for your template"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
            </div>

            {/* Save Type Selector */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    What to save
                </label>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setSaveType('caption')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
                            saveType === 'caption'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <FaPencilAlt size={14} />
                        Caption
                        {saveType === 'caption' && <FaCheck size={12} className="text-blue-500" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setSaveType('hashtag')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
                            saveType === 'hashtag'
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <FaHashtag size={14} />
                        Hashtags
                        {saveType === 'hashtag' && <FaCheck size={12} className="text-purple-500" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setSaveType('both')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all ${
                            saveType === 'both'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        Both
                        {saveType === 'both' && <FaCheck size={12} className="text-green-500" />}
                    </button>
                </div>
            </div>

            {/* Preview */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                </label>
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-[200px] overflow-y-auto">
                    {(saveType === 'caption' || saveType === 'both') && (
                        <div className="mb-3">
                            <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                <FaPencilAlt size={10} /> Caption
                            </p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {getCaptionContent() || <span className="text-gray-400 italic">No caption available</span>}
                            </p>
                        </div>
                    )}
                    {(saveType === 'hashtag' || saveType === 'both') && (
                        <div>
                            <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                <FaHashtag size={10} /> Hashtags
                            </p>
                            <p className="text-sm text-blue-600">
                                {getHashtagsContent() || <span className="text-gray-400 italic">No hashtags available</span>}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-2">
                <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || !templateName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                    {isSaving ? 'Saving...' : 'Save Template'}
                </button>
            </div>
        </div>
    );
};

export default SaveViralModal;
